import React, { useEffect, useRef } from 'react';
import { Plus, Trash2 } from 'lucide-react';

/**
 * Card list for useFieldArray.
 *
 *   const { fields, append, remove } = useFieldArray({ control, name: 'employers' });
 *   <RepeatableGroup
 *     items={fields}
 *     title={(i) => `Employer ${i + 1}`}
 *     addLabel="Add another employer"
 *     onAdd={() => append(emptyEmployer())}
 *     onRemove={remove}
 *     minItems={1}
 *     renderItem={(index) => <EmployerFields index={index} />}
 *   />
 */
const RepeatableGroup = ({
  items = [],
  title,
  renderItem,
  onAdd,
  onRemove,
  addLabel = 'Add another',
  minItems = 0,
  maxItems = 20,
  emptyHint,
  disabled = false,
  itemError
}) => {
  const previousCount = useRef(items.length);
  const containerRef = useRef(null);

  // Focus the first input of a newly added card.
  useEffect(() => {
    if (items.length > previousCount.current && containerRef.current) {
      const cards = containerRef.current.querySelectorAll('[data-repeatable-card]');
      const last = cards[cards.length - 1];
      const firstInput = last?.querySelector('input, select, textarea, button[role="radio"]');
      if (firstInput) {
        firstInput.focus({ preventScroll: false });
        last.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
    previousCount.current = items.length;
  }, [items.length]);

  return (
    <div ref={containerRef} className="space-y-4">
      {items.length === 0 && emptyHint && (
        <p className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-500">{emptyHint}</p>
      )}

      {items.map((item, index) => {
        const canRemove = !disabled && items.length > minItems;
        const error = typeof itemError === 'function' ? itemError(index) : null;
        return (
          <div
            key={item.id || index}
            data-repeatable-card
            className={`rounded-lg border bg-white shadow-sm ${error ? 'border-red-300' : 'border-gray-200'}`}
          >
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
              <h4 className="text-sm font-semibold text-gray-900">
                {typeof title === 'function' ? title(index, item) : `${title || 'Item'} ${index + 1}`}
              </h4>
              {canRemove && (
                <button
                  type="button"
                  onClick={() => onRemove(index)}
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm text-gray-500 hover:bg-red-50 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-400"
                  aria-label={`Remove ${typeof title === 'function' ? title(index, item) : `item ${index + 1}`}`}
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                  <span className="hidden sm:inline">Remove</span>
                </button>
              )}
            </div>
            <div className="p-4">
              {error && <p className="mb-3 text-sm text-red-600" role="alert">{error}</p>}
              {renderItem(index, item)}
            </div>
          </div>
        );
      })}

      {!disabled && items.length < maxItems && (
        <button
          type="button"
          onClick={onAdd}
          className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-blue-300 bg-blue-50/40 px-4 py-3 text-sm font-medium text-blue-700 hover:border-blue-500 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          {addLabel}
        </button>
      )}
    </div>
  );
};

export default RepeatableGroup;
